import { BaseWorkspaceRole, PermissionResource } from 'callabo-api/src';

// change to enum
const sortedPredefinedRoleTypes: Record<BaseWorkspaceRole, number> = {
  [BaseWorkspaceRole.WORKSPACE_MANAGER]: 0,
  [BaseWorkspaceRole.ARCHIVIST]: 1,
  [BaseWorkspaceRole.VIEWER]: 2,
  [BaseWorkspaceRole.DEACTIVATED]: 3,
};

export function compareWorkspaceBaseRole(a: BaseWorkspaceRole, b: BaseWorkspaceRole) {
  const aIndex = sortedPredefinedRoleTypes[a];
  const bIndex = sortedPredefinedRoleTypes[b];
  return bIndex - aIndex;
}

export type PermissionEnum = PermissionResource;
export const PermissionEnum = PermissionResource;
export const PERMISSIONS = [
  PermissionEnum.MEMBER_MANAGEMENT,
  PermissionEnum.WORKSPACE_MANAGEMENT,
  PermissionEnum.PAYMENT_MANAGEMENT,
  PermissionEnum.VIEW_ALL_MEETINGS,
  PermissionEnum.DELETE_MEETINGS_FROM_WORKSPACE,
  PermissionEnum.ARCHIVE_MEETINGS,
  PermissionEnum.CHATBOT_FEATURES,
  PermissionEnum.TEAM_MANAGEMENT,
  PermissionEnum.TEAM_MEMBER_MANAGEMENT,
  PermissionEnum.DELETE_MEETINGS_FROM_TEAM,
  PermissionEnum.EDIT_MEETING_VIEW_SCOPE,
  PermissionEnum.EDIT_MEETING_PROPERTIES,
  PermissionEnum.EXTERNAL_MEETING_SHARING,
  PermissionEnum.RESET_LANGUAGE,
  PermissionEnum.AUTOMATION_INTEGRATION,
  PermissionEnum.SHARE_INTEGRATION,
  PermissionEnum.VIEW_MEETINGS,
  PermissionEnum.ALL_ARCHIVE_MEETINGS,
  PermissionEnum.ALL_EDIT_MEETING_PROPERTIES,
  PermissionEnum.ALL_EXTERNAL_MEETING_SHARING,
  PermissionEnum.ALL_SHARE_INTEGRATION,
  PermissionEnum.WORKSPACE_INTEGRATION,
  PermissionEnum.VIEW_ALL_TEAM_MEETINGS,
];
export type PermissionScope = 'workspace' | 'team';
export const permissionScopeMap: Record<PermissionEnum, PermissionScope> = {
  member_management: 'workspace',
  workspace_management: 'workspace',
  payment_management: 'workspace',
  view_all_meetings: 'workspace',
  delete_meetings_from_workspace: 'workspace',
  archive_meetings: 'workspace',
  chatbot_features: 'workspace',
  team_management: 'team',
  team_member_management: 'team',
  delete_meetings_from_team: 'team',
  edit_meeting_view_scope: 'team',
  edit_meeting_properties: 'team',
  external_meeting_sharing: 'team',
  reset_language: 'team',
  automation_integration: 'team',
  share_integration: 'team',
  view_meetings: 'team',
  all_archive_meetings: 'workspace',
  all_edit_meeting_properties: 'workspace',
  all_external_meeting_sharing: 'workspace',
  all_share_integration: 'workspace',
  workspace_integration: 'workspace',
  view_all_team_meetings: 'team',
};

type PermissionType = 'accept' | 'deny' | 'not_set' | 'accept_workspace';

const WorkspaceManagerPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'accept_workspace',
  workspace_management: 'accept_workspace',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'accept',
  chatbot_features: 'accept_workspace',
  team_management: 'accept_workspace',
  team_member_management: 'accept_workspace',
  delete_meetings_from_team: 'accept_workspace',
  edit_meeting_view_scope: 'accept_workspace',
  edit_meeting_properties: 'accept',
  external_meeting_sharing: 'accept',
  reset_language: 'accept_workspace',
  automation_integration: 'not_set',
  share_integration: 'accept',
  view_meetings: 'accept_workspace',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'accept_workspace',
  view_all_team_meetings: 'not_set',
};

const MeetingEditorPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'accept',
  chatbot_features: 'accept_workspace',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'not_set',
  edit_meeting_view_scope: 'not_set',
  edit_meeting_properties: 'accept',
  external_meeting_sharing: 'accept',
  reset_language: 'not_set',
  automation_integration: 'not_set',
  share_integration: 'accept',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const ViewerPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'not_set',
  edit_meeting_view_scope: 'not_set',
  edit_meeting_properties: 'not_set',
  external_meeting_sharing: 'not_set',
  reset_language: 'not_set',
  automation_integration: 'not_set',
  share_integration: 'not_set',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const TeamManagerPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'accept_workspace',
  team_member_management: 'accept_workspace',
  delete_meetings_from_team: 'not_set',
  edit_meeting_view_scope: 'not_set',
  edit_meeting_properties: 'not_set',
  external_meeting_sharing: 'not_set',
  reset_language: 'not_set',
  automation_integration: 'accept_workspace',
  share_integration: 'not_set',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const MeetingManagerPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'accept_workspace',
  delete_meetings_from_workspace: 'accept_workspace',
  archive_meetings: 'accept_workspace',
  chatbot_features: 'accept_workspace',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'accept_workspace',
  edit_meeting_view_scope: 'accept_workspace',
  edit_meeting_properties: 'accept_workspace',
  external_meeting_sharing: 'accept_workspace',
  reset_language: 'accept_workspace',
  automation_integration: 'not_set',
  share_integration: 'accept_workspace',
  view_meetings: 'accept',
  all_archive_meetings: 'accept_workspace',
  all_edit_meeting_properties: 'accept_workspace',
  all_external_meeting_sharing: 'accept_workspace',
  all_share_integration: 'accept_workspace',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const PaymentManagerPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'accept_workspace',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'not_set',
  edit_meeting_view_scope: 'not_set',
  edit_meeting_properties: 'not_set',
  external_meeting_sharing: 'not_set',
  reset_language: 'not_set',
  automation_integration: 'not_set',
  share_integration: 'not_set',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const TeamLeaderPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'accept',
  team_member_management: 'accept',
  delete_meetings_from_team: 'accept',
  edit_meeting_view_scope: 'accept',
  edit_meeting_properties: 'accept',
  external_meeting_sharing: 'accept',
  reset_language: 'accept',
  automation_integration: 'accept',
  share_integration: 'accept',
  view_meetings: 'accept',
  all_archive_meetings: 'accept',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const TeamEditorPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'not_set',
  edit_meeting_view_scope: 'not_set',
  edit_meeting_properties: 'accept',
  external_meeting_sharing: 'accept',
  reset_language: 'not_set',
  automation_integration: 'not_set',
  share_integration: 'not_set',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

const TeamViewerPermission: Record<PermissionEnum, PermissionType> = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'not_set',
  edit_meeting_view_scope: 'not_set',
  edit_meeting_properties: 'not_set',
  external_meeting_sharing: 'not_set',
  reset_language: 'not_set',
  automation_integration: 'not_set',
  share_integration: 'not_set',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'not_set',
};

export const TeamMeetingManagerPermission = {
  member_management: 'not_set',
  workspace_management: 'not_set',
  payment_management: 'not_set',
  view_all_meetings: 'not_set',
  delete_meetings_from_workspace: 'not_set',
  archive_meetings: 'not_set',
  chatbot_features: 'not_set',
  team_management: 'not_set',
  team_member_management: 'not_set',
  delete_meetings_from_team: 'accept',
  edit_meeting_view_scope: 'accept',
  edit_meeting_properties: 'accept',
  external_meeting_sharing: 'accept',
  reset_language: 'not_set',
  automation_integration: 'not_set',
  share_integration: 'not_set',
  view_meetings: 'accept',
  all_archive_meetings: 'not_set',
  all_edit_meeting_properties: 'not_set',
  all_external_meeting_sharing: 'not_set',
  all_share_integration: 'not_set',
  workspace_integration: 'not_set',
  view_all_team_meetings: 'accept',
};

export type RolePermissions = Array<{
  permissions: Record<PermissionEnum, PermissionType>;
}>;

export const predefinedRolePermissions = [
  WorkspaceManagerPermission,
  MeetingEditorPermission,
  ViewerPermission,
  TeamManagerPermission,
  MeetingManagerPermission,
  PaymentManagerPermission,
  TeamLeaderPermission,
  TeamEditorPermission,
  TeamViewerPermission,
  TeamMeetingManagerPermission,
];
